import { useState } from "react";
import { toast } from "sonner";
import { mask } from '../lib/privacy';

const API = process.env.REACT_APP_BACKEND_URL;
const TEAL = "var(--pr-teal)";
const INK = "var(--pr-ink)";
const INK2 = "var(--pr-ink2)";
const MUTED = "var(--pr-muted)";
const GREEN = "var(--pr-green)";

const fmt = (n) => mask(`$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const shortDate = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-").map(Number);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[m - 1]} ${d}`;
};

const FIELDS = [
  { key: "pto", label: "PTO fund", emoji: "🌴", note: "grows your PTO bank" },
  { key: "savings", label: "Savings", emoji: "🎯" },
  { key: "debt", label: "Debt payment", emoji: "💳" },
];

const TAX_OPTIONS = [
  { value: "planned", label: "Yes, I moved the planned amount" },
  { value: "different", label: "I moved a different amount" },
  { value: "not_yet", label: "Not yet" },
];

const DiffChip = ({ planned, actual }) => {
  if (planned == null || actual === "" || actual == null) return null;
  const d = Math.round((parseFloat(actual) - planned) * 100) / 100;
  if (Math.abs(d) < 0.005) return <span className="text-[10px] font-bold" style={{ color: GREEN }} data-testid="diff-matched">matched ✓</span>;
  // Neutral by design: never show a recorded shortfall as a red "under"/debt balance.
  return null;
};

export const ActualsCard = ({ prompt, required, onSaved }) => {
  const planned = prompt.planned || prompt.ideal || {};
  // Everyone can record a set-aside toward any goal this week, even one they didn't
  // plan for in the calculator — an unplanned win still counts.
  const visible = FIELDS;
  const [vals, setVals] = useState(() => Object.fromEntries(visible.map((f) => [f.key, ""])));
  const [taxStatus, setTaxStatus] = useState("");
  const [taxAmount, setTaxAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [recorded, setRecorded] = useState(null);
  const [snoozed, setSnoozed] = useState(false);
  const plannedTax = planned.tax;
  const received = prompt.received;
  const awaiting = prompt.awaiting;
  const earned = prompt.earned;
  const awaitingOnly = !!prompt.awaiting_only;

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    const body = { week_start: prompt.week_start };
    let any = false;
    for (const f of visible) {
      const raw = vals[f.key];
      if (raw !== "" && raw != null) {
        body[f.key] = Math.max(parseFloat(raw) || 0, 0);
        any = true;
      }
    }
    if (taxStatus === "planned" && plannedTax != null) {
      body.tax = plannedTax;
      body.tax_status = "planned";
      any = true;
    } else if (taxStatus === "different") {
      if (taxAmount === "" || taxAmount == null) {
        toast.error("Tell us the tax amount you moved");
        return;
      }
      body.tax = Math.max(parseFloat(taxAmount) || 0, 0);
      body.tax_status = "different";
      any = true;
    } else if (taxStatus === "not_yet") {
      body.tax_status = "not_yet";
      any = true;
    }
    if (!any) {
      toast.error("Enter at least one amount, $0 is honest too");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/actuals`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(typeof data.detail === "string" ? data.detail : "Couldn't save, try again"); return; }
      // Calm confirmation: echo exactly what was recorded, nothing more.
      const parts = [];
      for (const f of visible) if (body[f.key] != null) parts.push(`${f.label} ${fmt(body[f.key])}`);
      if (body.tax != null) parts.push(`Tax ${fmt(body.tax)}`);
      else if (body.tax_status === "not_yet") parts.push("Tax: not yet");
      setRecorded(parts);
      toast.success(`Recorded ✓${parts.length ? " — " + parts.join(" · ") : ""}`);
      onSaved(data.bank);
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      id="actuals-card"
      onSubmit={save}
      className="rounded-2xl p-5 lg:p-6 pr-card"
      data-testid="actuals-card"
    >
      <p className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: TEAL }}>
        🧾 Last relay {prompt.relay_number ? `· Relay ${prompt.relay_number}` : ""} · {shortDate(prompt.week_start)} – {shortDate(prompt.week_end)}
      </p>

      {recorded ? (
        <div className="mt-1" data-testid="actuals-recorded">
          <p className="text-sm font-bold" style={{ color: GREEN }}>Recorded ✓</p>
          <p className="text-[11px] mt-0.5" style={{ color: INK2 }}>
            {recorded.length ? recorded.join(" · ") : "Nothing set aside recorded."} — by your own records; the app can't verify a bank transfer.
          </p>
        </div>
      ) : snoozed ? (
        <div className="mt-1" data-testid="actuals-snoozed">
          <p className="text-sm font-bold" style={{ color: INK }}>Not yet — saved for later.</p>
          <p className="text-[11px] mt-0.5" style={{ color: INK2 }}>
            No amounts recorded. You can close this out any time, nothing is blocked.
          </p>
          <button type="button" onClick={() => setSnoozed(false)}
            className="mt-2 text-xs font-bold underline" style={{ color: TEAL }} data-testid="actuals-reopen-btn">
            Record it now
          </button>
        </div>
      ) : (
      <>
      {/* Neutral earned / received / awaiting summary — the work week can simply end */}
      {(earned != null) && (
        <p className="text-[11px] mb-1" style={{ color: INK2 }} data-testid="actuals-week-summary">
          Earned <b style={{ color: INK }}>{fmt(earned)}</b>
          {received != null ? <> · Received <b style={{ color: INK }}>{fmt(received)}</b></> : null}
          {awaiting > 0.004 ? <> · <span style={{ color: MUTED }}>{fmt(awaiting)} awaiting payout</span></> : null}
        </p>
      )}

      {awaitingOnly ? (
        <div data-testid="actuals-awaiting-only">
          <p className="text-sm font-bold mb-1" style={{ color: INK }} data-testid="actuals-headline">
            Nothing to set aside yet — this relay is awaiting payout.
          </p>
          <p className="text-[11px] leading-snug" style={{ color: INK2 }}>
            Your earned target still counts. When the money lands, record it under
            <b style={{ color: INK }}> Awaiting payment</b> and we'll ask what you set aside then.
            Nothing to do now.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              if (busy) return;
              setBusy(true);
              try {
                const res = await fetch(`${API}/api/caddy/actuals`, {
                  method: "POST",
                  credentials: "include",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ week_start: prompt.week_start, tax_status: "not_yet" }),
                });
                const data = await res.json();
                if (!res.ok) { toast.error(typeof data.detail === "string" ? data.detail : "Couldn't save, try again"); return; }
                setSnoozed(true);
                toast.success("Marked reviewed ✓");
                onSaved(data.bank);
              } catch {
                toast.error("Network hiccup, try again.");
              } finally {
                setBusy(false);
              }
            }}
            className="mt-3 text-xs font-bold underline"
            style={{ color: TEAL }}
            data-testid="actuals-mark-reviewed-btn"
          >
            {busy ? "Saving…" : "Mark reviewed — nothing to set aside yet"}
          </button>
        </div>
      ) : (
      <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
        aria-expanded={open}
        data-testid="actuals-toggle"
      >
        <span className="text-sm font-bold" style={{ color: INK }} data-testid="actuals-headline">
          {received != null
            ? `From the ${fmt(received)} you were paid, what did you set aside?`
            : "What did you set aside for your goals last week?"}
        </span>
        <span className="text-xs font-bold flex-shrink-0" style={{ color: TEAL }}>
          {open ? "Hide" : "Open"}
        </span>
      </button>

      {open && (
      <div className="mt-3">

      {/* Tax handoff, behavior check, not tax advice */}
      <div className="rounded-xl p-4 mb-4" style={{ background: "var(--pr-bg)" }} data-testid="actuals-tax-block">
        <p className="text-xs font-bold mb-0.5" style={{ color: INK }}>
          💸 Did you move your tax set-aside?
          {plannedTax != null && (
            <span className="ml-1.5 font-normal text-[10px]" style={{ color: MUTED }}>planned {fmt(plannedTax)}</span>
          )}
        </p>
        <p className="text-[10px] mb-2" style={{ color: MUTED }}>
          A handoff check, not a tax bill. Your sheet keeps the running estimate.
        </p>
        <div className="space-y-1.5">
          {TAX_OPTIONS.filter((o) => o.value !== "planned" || plannedTax != null).map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: INK2 }}>
              <input
                type="radio"
                name="tax-status"
                checked={taxStatus === o.value}
                onChange={() => setTaxStatus(o.value)}
                data-testid={`tax-status-${o.value}`}
              />
              {o.label}
              {o.value === "planned" && plannedTax != null && (
                <b style={{ color: INK }}>{fmt(plannedTax)}</b>
              )}
            </label>
          ))}
        </div>
        {taxStatus === "different" && (
          <div className="relative mt-2">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold" style={{ color: MUTED }}>$</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={taxAmount}
              onChange={(e) => setTaxAmount(e.target.value)}
              placeholder="Amount you moved"
              className="w-full pl-7 pr-20 py-2.5 rounded-full pr-input text-sm font-semibold"
              style={{ color: INK }}
              data-testid="actuals-tax-input"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2"><DiffChip planned={plannedTax} actual={taxAmount} /></span>
          </div>
        )}
        {taxStatus === "not_yet" && (
          <p className="text-[10px] mt-1.5" style={{ color: MUTED }} data-testid="tax-pending-note">
            We'll mark it pending. You can update it any time from the Time Off &amp; closeout history.
          </p>
        )}
      </div>

      <div className="space-y-2 mb-3">
        {visible.map((f) => (
          <div key={f.key} className="flex items-center gap-2">
            <label className="w-32 text-xs font-semibold flex-shrink-0" style={{ color: INK }}>
              {f.emoji} {f.label}
              {planned[f.key] != null ? (
                <span className="block text-[10px] font-normal" style={{ color: MUTED }}>planned {fmt(planned[f.key])}</span>
              ) : (
                <span className="block text-[10px] font-normal" style={{ color: MUTED }}>extra — optional</span>
              )}
            </label>
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold" style={{ color: MUTED }}>$</span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={vals[f.key]}
                onChange={(e) => setVals((p) => ({ ...p, [f.key]: e.target.value }))}
                placeholder={planned[f.key] != null ? String(planned[f.key]) : "0.00"}
                className="w-full pl-7 pr-20 py-2.5 rounded-full pr-input text-sm font-semibold"
                style={{ color: INK }}
                data-testid={`actuals-${f.key}-input`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2"><DiffChip planned={planned[f.key]} actual={vals[f.key]} /></span>
            </div>
          </div>
        ))}
      </div>
      <button
        type="submit"
        disabled={busy}
        className="w-full py-3 rounded-full font-bold text-sm disabled:opacity-40 pr-btn-primary"
        data-testid="actuals-save-btn"
      >
        {busy ? "Saving…" : "Close out relay ✓"}
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); setSnoozed(true); }}
        className="w-full py-2 mt-2 text-xs font-semibold underline"
        style={{ color: MUTED }}
        data-testid="actuals-notyet-btn"
      >
        Not yet — I'll record this later
      </button>
      </div>
      )}
      </>
      )}
      </>
      )}
    </form>
  );
};
